// People who signed in and are waiting to be let in.
//
// Signing in to the tenant no longer creates an account here: an identity
// nobody recognises is recorded as pending with no roles and refused a session
// (findPersonForSignIn in src/index.js). This is where an administrator or HR
// decides, and the decision has to include a role, because approving without
// one would put the person back in exactly the state this replaced.
//
// So the role picker comes first and Approve stays disabled until it is set,
// rather than letting somebody press Approve and then explaining why it was
// refused. Results appear on the row that was acted on, not at the top of the
// page.
import { useEffect, useState } from 'react';
import api from '../../hooks/useApi';

// Mirrors ASSIGNABLE_ROLES in src/api/signinApproval.js, which a test keeps in
// step with the PERMISSIONS vocabulary in utils/rbac.js.
const ROLES = [
  'admin', 'partner', 'commercial-lead', 'bd', 'hr', 'pm', 'pmo',
  'finance', 'billing', 'engineer', 'contractor', 'inventory-manager', 'qms_manager',
];

// 'bd' is BD, not Bd. Acronyms stay upper case; everything else is sentence case.
const ACRONYMS = new Set(['bd', 'hr', 'pm', 'pmo', 'qms']);
const label = (r) => r
  .split(/[-_]/)
  .map((word, i) => (ACRONYMS.has(word)
    ? word.toUpperCase()
    : (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)))
  .join(' ');

function when(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T') + (String(value).endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function AccessRequests({ canApprove, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [roles, setRoles]       = useState({});   // person id -> role
  const [busy, setBusy]         = useState(null);
  const [errors, setErrors]     = useState({});   // person id -> message
  const [confirming, setConfirming] = useState(null);

  const load = () => {
    if (!canApprove) return;
    api.get('access-requests')
      .then(d => setRequests(d?.requests || []))
      .catch(() => setRequests([]));
  };
  useEffect(load, [canApprove]);

  if (!canApprove || !requests.length) return null;

  const act = async (person, path, body) => {
    setBusy(person.id);
    setErrors(e => ({ ...e, [person.id]: null }));
    try {
      await api.post(`access-requests/${person.id}/${path}`, body);
      setRequests(rs => rs.filter(r => r.id !== person.id));
      setConfirming(null);
      // The list beside this panel still shows them as awaiting approval, and
      // a list that contradicts the thing you just did reads as the action not
      // having worked.
      onChanged?.();
    } catch (err) {
      setErrors(e => ({ ...e, [person.id]: err?.message || 'That did not go through. Try again.' }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded mb-2 overflow-hidden">
      <div className="px-3 py-1.5 border-b border-blue-200 bg-blue-50 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[9px] font-bold text-blue-800">
          Waiting for access ({requests.length})
        </span>
        <span className="text-[8px] text-blue-700">
          They signed in with Microsoft and have no account here yet. Give them a role to let them in.
        </span>
      </div>

      {requests.map(person => {
        const name = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email_work;
        const chosen = roles[person.id] || '';
        const isBusy = busy === person.id;
        return (
          <div key={person.id} className="px-3 py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold text-navy truncate">{name}</div>
                <div className="text-[8px] text-gray-500 mt-0.5">
                  {person.email_work}
                  {when(person.created_at) && <> &middot; first signed in {when(person.created_at)}</>}
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <select
                  value={chosen}
                  disabled={isBusy}
                  onChange={e => setRoles(r => ({ ...r, [person.id]: e.target.value }))}
                  className="text-[9px] border border-gray-200 rounded px-1.5 py-1 bg-white text-navy"
                  aria-label={`Role for ${name}`}
                >
                  <option value="">Choose a role…</option>
                  {ROLES.map(r => <option key={r} value={r}>{label(r)}</option>)}
                </select>
                <button
                  onClick={() => act(person, 'approve', { roles: chosen })}
                  disabled={!chosen || isBusy}
                  title={chosen ? '' : 'Choose a role first'}
                  className="px-2 py-1 text-[9px] font-semibold rounded bg-navy text-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isBusy ? 'Working…' : 'Approve'}
                </button>
                <button
                  onClick={() => setConfirming(person.id)}
                  disabled={isBusy}
                  className="px-2 py-1 text-[9px] font-semibold rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Turn away
                </button>
              </div>
            </div>

            {confirming === person.id && (
              <div className="mt-1.5 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[8px] text-red-800">
                  Remove {name}? They keep their Microsoft account and can sign in again, which puts them back on this list.
                </span>
                <span className="flex gap-1.5">
                  <button
                    onClick={() => act(person, 'reject', {})}
                    className="px-2 py-0.5 text-[8px] font-semibold rounded bg-red-700 text-white"
                  >
                    Remove them
                  </button>
                  <button
                    onClick={() => setConfirming(null)}
                    className="px-2 py-0.5 text-[8px] font-semibold rounded border border-gray-200 text-gray-600 bg-white"
                  >
                    Keep waiting
                  </button>
                </span>
              </div>
            )}

            {errors[person.id] && (
              <div className="mt-1.5 text-[8px] text-red-700">{errors[person.id]}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
